// AppLock.jsx — ekrani i kyçjes, pyetja e hyrjes së parë, dhe çelësi te
// "Siguria e llogarisë".
//
// E gjithë gjendja jeton këtu. App.jsx e vendos komponentin njëherë dhe nuk
// merret më me të; e vetmja gjë që i kthehet është `onLockedChange`, që faqja
// poshtë të mos vizatohet kur brava është e mbyllur.
//
// KUFIRI, i thënë hapur: kjo është një shtresë mbi aplikacionin, jo kriptim.
// Shih komentin në krye të `appLock.js`.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import {
  isNative, GRACE_MS, fetchMode, capability, authenticate,
  isEnabledOnDevice, setEnabledOnDevice, wasAsked, markAsked,
  setAccount, CHANGED, authenticateCode,
  markUnlocked, clearUnlocked, withinGrace,
} from "./appLock";
import { supabase } from "./supabaseClient";
import { provaNga, shenoBraven, shenoHap } from "./prova.jsx"; // ⚠ I PËRKOHSHËM — hiqet bashkë me `prova.jsx`
import { SAFE_TOP, SAFE_BOTTOM } from "./zonaESigurt"; // kapaku 64/48 — shih shënimin atje

// ---------------------------------------------------------------------------
// Ekrani i kyçjes
// ---------------------------------------------------------------------------
// I njëjti sfond si te ekrani i hyrjes. Kërkesë e userit, dhe ka të drejtë:
// brava është pjesë e së njëjtës derë, ndaj s'ka pse të duket si dritare tjetër.
// `rpc_get_login_settings` është publike — lexohet edhe pa sesion, dhe këtu ka
// gjithmonë sesion, pra vjen sigurt.
function useLoginArt() {
  const [art, setArt] = useState(null);
  useEffect(() => {
    let alive = true;
    supabase.rpc("rpc_get_login_settings").then(({ data, error }) => {
      if (!alive || error || !data) return;
      const d = Array.isArray(data) ? data[0] : data;
      setArt({
        image: d.art_image || "",
        imageDesktop: d.art_image_desktop || "",
        scrim: typeof d.scrim === "number" ? d.scrim : 30,
      });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return art;
}

// Pa rreth, pa titull, pa buton të madh — vetëm ilustrimi dhe një rresht poshtë.
// Ekrani i mëparshëm shpjegonte me fjalë diçka që sistemi e thotë vetë me
// dritaren e vet; ishte një faqe e tërë për të mos thënë asgjë.
function LockScreen({ C, F, t, onRetry, onCode, busy, failed, cap, codeOnly }) {
  const art = useLoginArt();
  const wide = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(min-width:900px)").matches : false;
  const src = art ? ((wide ? (art.imageDesktop || art.image) : (art.image || art.imageDesktop)) || "") : "";

  const link = {
    background: "none", border: "none", padding: "6px 8px", cursor: "pointer",
    fontFamily: F.body, fontSize: 13, color: C.ink, opacity: 0.85,
  };

  return (
    <div style={{
      // `var(--bgDeep)` e jo `C.bgDeep`: kur cache-i i dizajnit mungon (Android
      // e pastron localStorage-in periodikisht), `COLORS` mbetet te tema klasike
      // dhe ky element, që mbulon krejt ekranin, do të pikturonte të kuqen.
      position: "fixed", inset: 0, zIndex: 100000, background: "var(--bgDeep, #1F2A1E)",
      display: "flex", flexDirection: "column", justifyContent: "flex-end",
    }}>
      {src && (
        <div style={{
          position: "absolute", inset: 0, backgroundImage: `url(${src})`,
          backgroundSize: "cover", backgroundPosition: "center", pointerEvents: "none",
        }} />
      )}
      <div style={{
        position: "absolute", inset: 0, background: "var(--bgDeep, #1F2A1E)", pointerEvents: "none",
        opacity: Math.max(0, Math.min(100, art ? art.scrim : 30)) / 100,
      }} />

      <div style={{
        position: "relative", textAlign: "center",
        padding: `0 20px calc(26px + ${SAFE_BOTTOM})`,
      }}>
        {busy ? (
          <span style={{ fontFamily: F.body, fontSize: 13, color: C.ink, opacity: 0.7 }}>{t("Waiting…")}</span>
        ) : (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            {codeOnly ? (
              /* Pas disa dështimesh, biometria hiqet nga rreshti: rihapja e së
                 njëjtës dritare do të ishte këmbëngulje pa kuptim. */
              <button onClick={onCode} style={{ ...link, opacity: 1, fontWeight: 700 }}>
                {t("Enter the code")}
              </button>
            ) : (
              <>
                <button onClick={onRetry} style={link}>
                  {failed ? t("Try again") : t("Unlock")}
                </button>
                {cap && cap.deviceIsSecure && (
                  <>
                    <span style={{ color: C.ink, opacity: 0.35, fontSize: 12 }}>·</span>
                    <button onClick={onCode} style={link}>{t("Enter the code")}</button>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pyetja e hyrjes së parë në këtë pajisje. Shfaqet vetëm kur admini e ka lënë
// zgjedhjen të lirë — kur është e detyrueshme, s'ka çfarë të pyesim.
// ---------------------------------------------------------------------------
function FirstRunSheet({ C, F, t, cap, onChoose }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999, background: "rgba(0,0,0,.55)",
      display: "flex", alignItems: "flex-end", justifyContent: "center",
    }}>
      <div style={{
        width: "100%", maxWidth: 460, background: C.panel, borderTopLeftRadius: 18, borderTopRightRadius: 18,
        padding: `22px 20px calc(22px + ${SAFE_BOTTOM})`, border: `1px solid ${C.goldSoft}`,
      }}>
        <div style={{ fontFamily: F.display || F.body, fontSize: 18, color: C.ink, marginBottom: 6 }}>
          {t("How should the app open?")}
        </div>
        <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkLight, lineHeight: 1.6, marginBottom: 16 }}>
          {t("The choice applies only to this device and can be changed any time in Account security.")}
        </div>

        <button onClick={() => onChoose(false)} style={{
          width: "100%", padding: "13px 14px", borderRadius: 12, marginBottom: 10, cursor: "pointer",
          background: C.gold, color: C.bgDeep, border: "none", textAlign: "left",
        }}>
          <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700 }}>{t("Open directly")}</div>
          <div style={{ fontFamily: F.body, fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>
            {t("Your phone already has its own code.")}
          </div>
        </button>

        <button onClick={() => onChoose(true)} disabled={!cap.usable} style={{
          width: "100%", padding: "13px 14px", borderRadius: 12, cursor: cap.usable ? "pointer" : "default",
          background: "transparent", color: C.ink, border: `1px solid ${C.goldSoft}`, textAlign: "left",
          opacity: cap.usable ? 1 : 0.5,
        }}>
          <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700 }}>
            {cap.biometry ? t("Ask for fingerprint or face") : t("Ask for the phone's PIN")}
          </div>
          <div style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkLight, marginTop: 2 }}>
            {cap.usable
              ? t("Every time the app opens after a pause.")
              : t("This device has neither a fingerprint nor a screen code.")}
          </div>
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Çelësi te "Siguria e llogarisë". Rri i fshehur kur admini s'e ka ndezur
// veçorinë, dhe bëhet vetëm tekst kur e ka bërë të detyrueshme — një çelës që
// nuk lëviz dot është më keq se asnjë çelës.
// ---------------------------------------------------------------------------
export function AppLockSetting({ C, F, t }) {
  const [mode, setMode] = useState("off");
  const [on, setOn] = useState(false);
  const [cap, setCap] = useState(null);

  const reload = useCallback(async () => {
    const m = await fetchMode();
    const c = await capability();
    const e = await isEnabledOnDevice();
    setMode(m); setCap(c); setOn(e);
  }, []);
  useEffect(() => {
    reload();
    // Ndryshimet nga fleta e hyrjes së parë duhet të duken këtu menjëherë.
    const h = () => reload();
    window.addEventListener(CHANGED, h);
    return () => window.removeEventListener(CHANGED, h);
  }, [reload]);

  // Heshtje kur s'ka çfarë të ofrohet: as admini s'e ka ndezur, ose pajisja s'e
  // mban. Një kuti që shpjegon pse mungon një çelës është vetë zhurmë.
  if (!isNative || !cap || mode === "off") return null;

  if (!cap.usable) {
    return (
      <div className="px-2.5 py-2.5 rounded-md" style={{ background: C.panelSoft, marginBottom: 12 }}>
        <p style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkLight }}>
          {t("This device has neither a fingerprint nor a screen code, so the lock does not apply.")}
        </p>
      </div>
    );
  }

  if (mode === "required") {
    return (
      <div className="px-2.5 py-2.5 rounded-md" style={{ background: C.panelSoft, marginBottom: 12 }}>
        <p style={{ fontFamily: F.body, fontSize: 13, color: C.ink }}>
          {t("🔒 Opening the app requires fingerprint, face or PIN — set by the platform administration.")}
        </p>
      </div>
    );
  }

  return (
    <label className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-md" style={{ background: C.panelSoft, marginBottom: 12, cursor: "pointer" }}>
      <input
        type="checkbox" checked={on}
        onChange={async (e) => { setOn(e.target.checked); await setEnabledOnDevice(e.target.checked); }}
        style={{ accentColor: C.gold, width: 15, height: 15 }}
      />
      <span style={{ fontFamily: F.body, fontSize: 13, color: C.ink }}>
        {cap.biometry ? t("Ask for fingerprint or face to open the app") : t("Ask for the phone's PIN to open the app")}
        <span style={{ display: "block", fontSize: 11, color: C.inkLight, marginTop: 2 }}>
          {t("Applies only to this device.")}
        </span>
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Komponenti kryesor
// ---------------------------------------------------------------------------
export default function AppLock({ session, C, F, t, onLockedChange }) {
  const [mode, setMode] = useState(null);      // null = ende s'dihet
  const [cap, setCap] = useState(null);
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // Pas kaq dështimesh radhazi, biometria hiqet nga rruga: dritarja e sistemit
  // nuk rihapet më vetë, dhe rreshti poshtë ofron vetëm kodin. Tre është numri
  // ku këmbëngulja kthehet në bezdi — kush s'ia doli tri herë, s'ia del as të katërtën.
  const FAILS_TO_CODE = 3;
  const [fails, setFails] = useState(0);
  const [askFirstRun, setAskFirstRun] = useState(false);
  // 0 = s'ka qenë kurrë në sfond. Vlera 0 NUK do të thotë "1 janar 1970" këtu:
  // pa këtë dallim, `Date.now() - 0` del sa gjysmë shekulli dhe rikyç menjëherë.
  const backgroundedAt = useRef(0);
  // Sa kohë rri hapur dritarja e sistemit për gjurmën, aplikacioni humbet fokusin.
  // Pa këtë flamur, ajo humbje lexohet si "shkoi në sfond" dhe kyçja rinis pikërisht
  // mbi shkyçjen që sapo u bë.
  const authing = useRef(false);
  // E shkyçur njëherë në këtë xhirim të aplikacionit.
  const unlockedOnce = useRef(false);
  // A e kemi hapur vetë dritaren e sistemit për këtë kyçje. Një herë, jo më:
  // nëse personi e mbyll, e rihap butoni, jo kodi.
  const autoTried = useRef(false);

  // NGA OBJEKTI TE IDENTITETI. Këtu ishte unaza kryesore: varësia ishte vetë
  // objekti `session`, dhe supabase-js e krijon një objekt TË RI sa herë
  // rifreskohet token-i ose lëshohet një ngjarje autentikimi. Çdo objekt i ri
  // e rixhironte efektin, dhe efekti kyçte sërish — pra sapo shkyçje, kyçej.
  // Identiteti i përdoruesit është ajo që ka vërtet rëndësi, dhe ai s'ndryshon.
  const userId = (session && session.user && session.user.id) || null;

  const report = useCallback((v) => {
    setLocked(v);
    if (onLockedChange) onLockedChange(v);
  }, [onLockedChange]);

  // `tick` e rixhiron zgjidhjen pa pritur një ngjarje autentikimi — përdoret nga
  // "Pyet përsëri", që butoni të bëjë atë që thotë në çast.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    shenoHap("efekti CHANGED: nis"); // ⚠ gjurmë e përkohshme
    const h = () => { shenoHap("CHANGED erdhi"); setTick((n) => n + 1); };
    window.addEventListener(CHANGED, h);
    return () => window.removeEventListener(CHANGED, h);
  }, []);

  useEffect(() => {
    let alive = true;
    shenoHap("efekti kryesor: nis · userId=" + (userId ? "po" : "jo")); // ⚠ gjurmë
    setAccount(userId);
    if (!isNative || !userId) {
      shenoHap("dalje: pa përdorues ose pa telefon");
      setMode("off"); report(false);
      unlockedOnce.current = false;   // dalje → xhirimi tjetër nis nga e para
      clearUnlocked();
      return;
    }
    (async () => {
      // RENDI KA RËNDËSI, dhe kjo është ndreqja e 28 gushtit.
      //
      // Më parë të dyja thirrjet bëheshin gjithmonë, dhe vetëm pastaj pyetej
      // nëse brava ekziston fare për këtë person. Pra çdo hyrje e pyeste
      // pajisjen për gjurmën edhe kur admini s'e kishte ndezur kurrë veçorinë —
      // punë pa asnjë qëllim, dhe pikërisht ajo punë e vrau aplikacionin.
      //
      // Tani: fjala e parë është e serverit. Nëse ai thotë "off", pajisja nuk
      // preket fare.
      shenoHap("fetchMode: nis");
      const m = await fetchMode();
      shenoHap("fetchMode → " + String(m));
      if (!alive) return;
      setMode(m);
      if (m === "off") { shenoHap("dalje: brava e fikur nga serveri"); report(false); return; }

      shenoHap("checkBiometry: nis");
      const c = await capability();
      shenoHap("checkBiometry → " + (c && c.usable ? "e përdorshme" : "jo"));
      if (!alive) return;
      setCap(c);
      if (!c.usable) { shenoHap("dalje: pajisja s'e mban"); report(false); return; }
      // `unlockedOnce` ndal VETËM rikyçjen, jo pyetjen. Më parë ndalonte të dyja,
      // dhe kështu "Pyet përsëri" nuk bënte gjë pasi kishe kaluar bravën njëherë.
      // Rifreskimi i faqes nuk është rinisje e aplikacionit. `unlockedOnce`
      // jeton në memorie dhe vdes me modulin; shenja e kohës e mbijeton.
      shenoHap("withinGrace: nis");
      const already = unlockedOnce.current || (await withinGrace());
      shenoHap("withinGrace → " + (already ? "brenda afatit" : "jashtë"));
      if (!alive) return;
      if (already) unlockedOnce.current = true;
      if (m === "required") { shenoHap("vendim: required · kyçur=" + (!already)); report(!already); return; }
      shenoHap("isEnabledOnDevice: nis");
      const enabled = await isEnabledOnDevice();
      shenoHap("isEnabledOnDevice → " + enabled);
      if (enabled) { shenoHap("vendim: optional-ndezur · kyçur=" + (!already)); report(!already); return; }
      // 'optional' dhe e pandezur: pyete, po s'e paskemi pyetur ende.
      shenoHap("wasAsked: nis");
      const asked = await wasAsked();
      shenoHap("wasAsked → " + asked);
      if (!alive) return;
      setAskFirstRun(!asked);
      shenoHap("vendim: pa kyçje · pyet=" + (!asked));
      report(false);
    })();
    return () => { alive = false; };
  }, [userId, report, tick]);

  const unlock = useCallback(async () => {
    setBusy(true); setFailed(false);
    authing.current = true;
    shenoHap("authenticate: nis"); // ⚠ gjurmë
    const ok = await authenticate({
      reason: t("Confirm it is you to open the tree."),
      cancel: t("Cancel"),
      title: t("Unlocking"),
    });
    shenoHap("authenticate → " + ok);
    authing.current = false;
    backgroundedAt.current = 0;
    setBusy(false);
    if (ok) { unlockedOnce.current = true; setFails(0); await markUnlocked(); report(false); }
    else { setFailed(true); setFails((n) => n + 1); }
  }, [report, t]);

  const unlockWithCode = useCallback(async () => {
    setBusy(true); setFailed(false);
    authing.current = true;
    shenoHap("authenticateCode: nis"); // ⚠ gjurmë
    const ok = await authenticateCode({
      reason: t("Enter the phone's code to open the tree."),
      cancel: t("Cancel"),
      title: t("Unlocking"),
    });
    authing.current = false;
    backgroundedAt.current = 0;
    setBusy(false);
    if (ok) { unlockedOnce.current = true; setFails(0); await markUnlocked(); report(false); } else setFailed(true);
  }, [report, t]);

  // HAPJA VETVETIU. Më parë ekrani i kyçjes priste një shtypje mbi "Shkyç" para
  // se të thërriste sistemin — një klikim që s'i shërbente askujt, sepse te ai
  // ekran s'ka gjë tjetër për të bërë. Tani dritarja e gjurmës/fytyrës hapet sapo
  // brava mbyllet. Butoni mbetet, por vetëm si rinisje pasi e ke anuluar njëherë.
  useEffect(() => {
    if (!locked) { autoTried.current = false; setFails(0); return; }
    if (autoTried.current) return;
    autoTried.current = true;
    shenoHap("hapja vetvetiu: thërret sistemin"); // ⚠ gjurmë
    if (fails >= FAILS_TO_CODE) unlockWithCode(); else unlock();
  }, [locked, unlock, unlockWithCode, fails]);

  // Rikyçja pas sfondit. Pa afatin e mëshirës, çdo dalje te galeria për një foto
  // do të kërkonte gjurmën në kthim — dhe veçoria do të hiqej brenda ditës.
  useEffect(() => {
    if (!isNative || !userId) return;
    let sub;
    (async () => {
      try {
        shenoHap("dëgjuesi i sfondit: nis"); // ⚠ gjurmë
        sub = await CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
          shenoHap("sfondi: isActive=" + isActive);
          if (authing.current) { backgroundedAt.current = 0; return; }
          if (!isActive) { backgroundedAt.current = Date.now(); return; }
          const at = backgroundedAt.current;
          backgroundedAt.current = 0;
          if (!at) return;                       // erdhëm në plan të parë pa qenë kurrë në sfond
          if (Date.now() - at < GRACE_MS) return;
          const m = await fetchMode();
          if (m === "required" || (m === "optional" && await isEnabledOnDevice())) {
            const c = await capability();
            if (c.usable) { unlockedOnce.current = false; await clearUnlocked(); report(true); }
          }
        });
        shenoHap("dëgjuesi i sfondit: u lidh");
      } catch (_e) { shenoHap("dëgjuesi i sfondit: dështoi"); }
    })();
    return () => { try { if (sub) sub.remove(); } catch (_e) { /* asgjë */ } };
  }, [userId, report]);

  // ── PROVA 8 ────────────────────────────────────────────────────────────
  //   ⚠ E PËRKOHSHME. Hiqet bashkë me `prova.jsx` (shih atje pse ekziston).
  //
  // Raportimi rri PARA daljeve: edhe kur brava s'ka çfarë të mbrojë dhe kthen
  // `null`, ne duam ta dimë se me çfarë përgjigjesh mbërriti aty.
  shenoBraven({
    mode,
    usable: cap ? !!cap.usable : null,
    locked,
    askFirstRun,
  });

  if (!isNative || !userId) return null;

  // Efektet më sipër kanë punuar të gjitha; kjo ndal vetëm vizatimin. Kështu
  // pyetja "faji te ajo që bën apo te ajo që tregon" merr përgjigje me një
  // ndërtim, pa e prekur asnjë rresht të logjikës.
  if (provaNga(8)) return null;

  if (locked) {
    return (
      <LockScreen
        C={C} F={F} t={t} busy={busy} failed={failed} cap={cap}
        codeOnly={fails >= FAILS_TO_CODE && !!(cap && cap.deviceIsSecure)}
        onRetry={unlock} onCode={unlockWithCode}
      />
    );
  }

  if (askFirstRun && cap) {
    return (
      <FirstRunSheet
        C={C} F={F} t={t} cap={cap}
        onChoose={async (on) => {
          setAskFirstRun(false);
          if (!on) { await setEnabledOnDevice(false); return; }
          // Provojmë menjëherë: nëse gjurma s'punon, është më mirë ta marrë vesh
          // tani sesa herën tjetër që hap aplikacionin dhe mbetet jashtë.
          authing.current = true;
          const ok = await authenticate({ reason: t("Confirm it is you to turn it on."), cancel: t("Cancel"), title: t("Unlocking") });
          authing.current = false;
          backgroundedAt.current = 0;
          if (ok) {
            // Sapo u identifikua — s'ka kuptim ta kyçim menjëherë pas kësaj.
            unlockedOnce.current = true;
            await markUnlocked();
            await setEnabledOnDevice(true);
          } else await markAsked();
        }}
      />
    );
  }

  return null;
}
